import { defineRouter } from '#q-app'
import { useAuthStore, useUsersStore, usePermissionsStore } from '@/stores/index.js'
import { createRouter, createMemoryHistory, createWebHistory, createWebHashHistory } from 'vue-router'

import routes from './routes.js'
import { handleChunkLoadError, clearChunkReloadGuard, registerChunkReloadListener } from './chunkReload.js'

export default defineRouter(() => {
  const authStore = useAuthStore()
  const usersStore = useUsersStore()
  const permissionsStore = usePermissionsStore()

  const createHistory = import.meta.env.QUASAR_SERVER
  ? createMemoryHistory
  : import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory

  const Router = createRouter({
    scroll: () => ({ y: 0 }),
    routes,

    // Leave these as is and change from quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    mode: import.meta.env.QUASAR_VUE_ROUTER_MODE,
    history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE),
    base: import.meta.env.QUASAR_VUE_ROUTER_BASE
  })
  Router.beforeEach((to) => {
    // Playground/diagnóstico offline-v2 (rutas solo-dev): bypass del guard.
    // Gated por APP_ENV===LOCAL, consistente con cómo se registran las rutas en
    // routes.js → en prod esta rama no existe.
    if (import.meta.env.APP_ENV === 'LOCAL' && (to.name === 'dev.offline-v2-playground' || to.name === 'dev.offline-v2-diagnostics' || to.name === 'dev.field-preview')) return true

    if (to.meta.public) return true

    const isAuthenticated = authStore.isAuthenticated
    const currentHas2fa = usersStore.currentHas2fa
    const user = {
      isLoaded: usersStore.currentIsLoaded,
      isOrganizationAdmin: usersStore.currentIsOrganizationAdmin,
      isAdmin: usersStore.currentIsAdmin,
      isSupervisor: usersStore.currentIsSupervisor,
      isSuperAdmin: usersStore.currentIsSuperAdmin,
      isLicencesManager: usersStore.currentIsLicencesManager,
      isPasswordManager: usersStore.currentIsPasswordManager,
      isUsersAdmin: usersStore.currentIsUsersAdmin
    }
    const route = {
      requiresAuth: to.matched.some(route => route.meta.requiresAuth),
      isAdmin: to.matched.some(route => route.meta.isAdmin),
      isOrganizationAdmin: to.matched.some(route => route.meta.isOrganizationAdmin),
      isSuperAdmin: to.matched.some(route => route.meta.isSuperAdmin),
      // Solo las rutas del modulo de licencias llevan esta marca (ver
      // superAdmin.js). Permite que un usuario role 12 sin ser super admin
      // entre a esas rutas puntuales sin abrirle el resto de /admin, que
      // sigue exigiendo isSuperAdmin real.
      isLicencesManager: to.matched.some(route => route.meta.isLicencesManager),
      isPasswordManager: to.matched.some(route => route.meta.isPasswordManager),
      requiredPermissions: to.matched.find(route => route.meta.requiredPermissions)?.meta.requiredPermissions
    }
    if (to.name === 'login' && (!route.requiresAuth || isAuthenticated)) {
      return true
    }

    if (!route.requiresAuth && isAuthenticated && !currentHas2fa) {
      return { name: 'login', query: { pendingView: to.path }, replace: true }
    }

    if (!route.requiresAuth && isAuthenticated) {
      return { name: 'navigation.home' }
    }

    if (route.requiresAuth && !isAuthenticated) {
      return { name: 'login', query: { pendingView: to.path }, replace: true }
    }

     if (user.isLoaded && isAuthenticated) {
      if (to.name === 'navigation.home' && user.isLicencesManager && !user.isSuperAdmin) {
        return { name: 'navigation.super-admin-licenses.index' }
      }
      if (!!route.requiredPermissions &&  permissionsStore.hasAccessTo(route.requiredPermissions)) {
        return true
      }
     // Permitir acceso a usuarios con isPasswordManager en rutas de admin-users
     const isUserRoute = to.path.includes("/admins/users");
     const hasPasswordManagerAccess = isUserRoute && user.isPasswordManager;

     const hasLicencesManagerAccess = route.isLicencesManager && user.isLicencesManager

     if (
       (route.isAdmin && !user.isAdmin && !hasPasswordManagerAccess) ||
       (route.isSuperAdmin && !user.isSuperAdmin && !hasLicencesManagerAccess) ||
       (route.isOrganizationAdmin && !user.isOrganizationAdmin)
     ) {
       return { name: "navigation.home" };
     }
   }
    return true
  })

  Router.onError((error, to) => {
    handleChunkLoadError(error, to)
  })

  // import() dinamicos fuera del router (tipos de item, PhotoEditor, dialogos, etc.)
  registerChunkReloadListener()

  Router.afterEach(() => {
    clearChunkReloadGuard()
  })

  return Router
})
